import { useEffect, useRef } from 'react';
import { trackEvent } from './analytics';
import { templatePath } from './appClickTracking';

/**
 * Connect funnel (CEL-3662 X5, Oct 23 2026). ONE event name, `connect_step`, for every state a connect
 * surface shows and every click on it, so the setup funnel reads end to end (card or chip shown →
 * click → setup page → back to chat). Same shape as `mcp_connect_step` (utils/mcpConnectFunnel.js).
 *
 *   surface    card | chip | page
 *   capability cowork | tools | keys | mcp | logins | triggers
 *   state      the state the surface was in (controlled words, e.g. needs_desktop_never, too_old, on)
 *   action     shown | the clicked action (download, open_desktop, update_app, turn_on, set_up, ...)
 *
 * Controlled identifiers only, never a folder, toolkit slug, key or server name (the app_click rule).
 * Registry: js/cellcog/TRACKING.md, "Connect surfaces".
 */
export const CONNECT_EVENT = 'connect_step';

// ConnectCardShell `kind` → capability.
const CAPABILITY_BY_KIND = {
  cowork: 'cowork',
  browse: 'browse',
  tool: 'tools',
  key: 'keys',
  mcp: 'mcp',
  login: 'logins',
  trigger: 'triggers',
};

export const capabilityForKind = (kind) => CAPABILITY_BY_KIND[kind] || kind || 'unknown';

/** A setup page's capability from its path (/cowork, /connectors/tools/x, ...), null when not one. */
export const capabilityForPath = (pathname) => {
  const p = String(pathname || '');
  if (p === '/cowork/browse' || p.startsWith('/cowork/browse/')) return 'browse';
  if (p === '/cowork' || p.startsWith('/cowork/')) return 'cowork';
  if (p.startsWith('/connectors/tools')) return 'tools';
  if (p.startsWith('/connectors/keys')) return 'keys';
  if (p.startsWith('/connectors/mcps')) return 'mcp';
  if (p.startsWith('/connectors/logins')) return 'logins';
  if (p.startsWith('/connectors/triggers')) return 'triggers';
  return null;
};

/**
 * `page_path` as a route TEMPLATE (review N5): ids collapse to `:id` (templatePath) and the setup pages
 * that carry a catalog slug in the path collapse too, so no toolkit or trigger slug is ever sent.
 */
export const connectPagePath = (pathname) => {
  const p = String(pathname || '');
  if (/^\/connectors\/tools\/[^/]+/.test(p)) return '/connectors/tools/:slug';
  if (/^\/connectors\/triggers\/[^/]+\/[^/]+/.test(p)) return '/connectors/triggers/:toolkit/:trigger';
  return templatePath(p);
};

export function trackConnect({ surface, capability, state, action }) {
  try {
    trackEvent(CONNECT_EVENT, {
      surface,
      capability,
      state: state || 'unknown',
      action,
      page_path: connectPagePath(window.location.pathname),
    });
  } catch (err) {
    /* analytics must never break product flows */
  }
}

/**
 * Fire `shown` once per mount per state. Pass `state = null` while the surface is hidden (a closed
 * chip panel): that resets the memory, so the next open counts as a new showing.
 */
export function useConnectShown(surface, capability, state) {
  const seen = useRef(new Set());
  useEffect(() => {
    if (!state) {
      seen.current.clear();
      return;
    }
    if (seen.current.has(state)) return;
    seen.current.add(state);
    trackConnect({ surface, capability, state, action: 'shown' });
  }, [surface, capability, state]);
}
